import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import FBIntegrationService from "../services/FBIntegrationService";
import AuthService from "../../Admin/services/AuthService";
import $ from "jquery";
import "datatables.net";
import {
  SaCard,
  SaPrimaryButton,
  SaGhostButton,
  SaFormActions,
  SaField,
  SaSectionTitle,
  saInputSx,
  SA,
} from "./shared/SuperAdminUi";

function shorten(val, max = 14) {
  if (val == null || val === "") return "—";
  const s = String(val);
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
}

function AddFBIntegtarion({ ClientId }) {
  const navigate = useNavigate();
  const toast = useRef(null);
  const tableRef = useRef(null);
  const [form, setForm] = useState({
    integrationName: "",
    appId: "",
    appSecret: "",
    accessToken: "",
    leadGenId: "",
    facebookPageId: "",
    isImportEnabled: true,
    facebookLeadImportFullData: false,
    facebookLeadImportDaysBack: "",
    clientId: ClientId,
  });
  const [formError, setFormError] = useState({});
  const [fbIntegrations, setFbIntegration] = useState([]);
  const [saving, setSaving] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [discoveredForms, setDiscoveredForms] = useState([]);
  const [discovering, setDiscovering] = useState(false);

  const fetchFbIntegrations = useCallback(async () => {
    if (!ClientId) return;
    try {
      const rows = await FBIntegrationService.getFBIntegrationsAsyncByClientId(ClientId);
      const list = Array.isArray(rows) ? rows : rows?.$values ?? [];
      setFbIntegration(list);
    } catch (error) {
      console.error("Error fetching FB integrations:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load Facebook integrations.",
        life: 4000,
      });
    }
  }, [ClientId]);

  useEffect(() => {
    fetchFbIntegrations();
  }, [fetchFbIntegrations]);

  useEffect(() => {
    const el = tableRef.current;
    if (!el || fbIntegrations.length === 0) return undefined;
    const t = setTimeout(() => {
      if ($.fn.DataTable.isDataTable(el)) {
        $(el).DataTable().destroy();
      }
      $(el).DataTable({ pageLength: 10, ordering: true });
    }, 0);
    return () => {
      clearTimeout(t);
      if ($.fn.DataTable.isDataTable(el)) {
        $(el).DataTable().destroy();
      }
    };
  }, [fbIntegrations]);

  const changeHandler = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormError((prev) => ({ ...prev, [name]: "" }));
  };

  async function saveFBIntegration() {
    const errors = {};
    ["appId", "appSecret", "accessToken", "leadGenId"].forEach((key) => {
      if (!String(form[key] ?? "").trim()) {
        errors[key] = "Required";
      }
    });
    setFormError(errors);
    if (Object.keys(errors).length > 0) return;

    const daysRaw = String(form.facebookLeadImportDaysBack ?? "").trim();
    const daysParsed = daysRaw === "" ? null : Number.parseInt(daysRaw, 10);
    if (daysRaw !== "" && (Number.isNaN(daysParsed) || daysParsed < 1)) {
      setFormError((prev) => ({ ...prev, facebookLeadImportDaysBack: "Enter a positive number or leave empty for system default." }));
      return;
    }

    const requestData = {
      integrationName: String(form.integrationName ?? "").trim(),
      appId: form.appId,
      appSecret: form.appSecret,
      accessToken: form.accessToken,
      leadGenId: form.leadGenId,
      facebookPageId: String(form.facebookPageId ?? "").trim(),
      isImportEnabled: form.isImportEnabled !== false,
      facebookLeadImportFullData: !!form.facebookLeadImportFullData,
      facebookLeadImportDaysBack: form.facebookLeadImportFullData ? null : daysParsed,
      isActive: true,
      clientId: ClientId,
    };
    setSaving(true);
    try {
      await FBIntegrationService.createFbIntegration(requestData);
      toast.current?.show({
        severity: "success",
        summary: "Saved",
        detail: "Facebook integration added.",
        life: 3000,
      });
      setForm({
        integrationName: "",
        appId: "",
        appSecret: "",
        accessToken: "",
        leadGenId: "",
        facebookPageId: "",
        isImportEnabled: true,
        facebookLeadImportFullData: false,
        facebookLeadImportDaysBack: "",
        clientId: ClientId,
      });
      setDiscoveredForms([]);
      setFormError({});
      await fetchFbIntegrations();
    } catch (error) {
      if (error.response?.status === 400 && error.response.data?.errors) {
        setFormError(error.response.data.errors);
      }
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Could not save integration.",
        life: 4000,
      });
      console.error(error);
    } finally {
      setSaving(false);
    }
  }

  const onSubmit = (e) => {
    e.preventDefault();
    saveFBIntegration();
  };

  function importModeLabel(row) {
    if (row.facebookLeadImportFullData) return "Full history";
    if (row.facebookLeadImportDaysBack != null && row.facebookLeadImportDaysBack > 0) {
      return `Last ${row.facebookLeadImportDaysBack} day(s)`;
    }
    return "System default days";
  }

  async function runFacebookImport(configId = null) {
    const userId = AuthService.getLoggedInUserId();
    if (!ClientId || !userId) {
      toast.current?.show({
        severity: "warn",
        summary: "Cannot import",
        detail: "Missing client or signed-in user.",
        life: 4000,
      });
      return;
    }
    const importKey = configId ?? "all";
    setImportingId(importKey);
    try {
      if (configId) {
        await FBIntegrationService.importFacebookLeadsForConfig(ClientId, configId, userId);
      } else {
        await FBIntegrationService.importFacebookLeads(ClientId, userId);
      }
      toast.current?.show({
        severity: "success",
        summary: "Import complete",
        detail: configId
          ? "Leads imported for this Lead Gen form (all ads on that form)."
          : "Leads imported from all enabled Lead Gen forms.",
        life: 4000,
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Import failed",
        detail: error.response?.data?.message ?? error.response?.data ?? "Could not import Facebook leads.",
        life: 5000,
      });
    } finally {
      setImportingId(null);
    }
  }

  async function discoverForms() {
    const pageId = String(form.facebookPageId ?? "").trim();
    const token = String(form.accessToken ?? "").trim();
    if (!pageId || !token) {
      toast.current?.show({
        severity: "warn",
        summary: "Discover forms",
        detail: "Enter Facebook Page ID and access token first.",
        life: 4000,
      });
      return;
    }
    setDiscovering(true);
    try {
      const forms = await FBIntegrationService.discoverLeadGenFormsPreview(pageId, token);
      setDiscoveredForms(Array.isArray(forms) ? forms : []);
      if (!forms?.length) {
        toast.current?.show({
          severity: "info",
          summary: "No active forms",
          detail: "Meta returned no active Lead Gen forms for this page.",
          life: 4000,
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Discover failed",
        detail: error.response?.data?.message ?? error.response?.data ?? "Could not load forms from Meta.",
        life: 5000,
      });
    } finally {
      setDiscovering(false);
    }
  }

  function applyDiscoveredForm(formRow) {
    setForm((prev) => ({
      ...prev,
      leadGenId: formRow.formId ?? formRow.FormId ?? prev.leadGenId,
      integrationName: prev.integrationName || formRow.name || formRow.Name || prev.integrationName,
    }));
  }

  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: "0 18px",
  };

  return (
    <div>
      <Toast ref={toast} />
      <form onSubmit={onSubmit}>
        <SaCard>
          <SaSectionTitle iconClass="fab fa-facebook">Add Lead Gen form source</SaSectionTitle>
          <p style={{ margin: "-8px 0 18px", fontSize: "13px", color: SA.muted }}>
            One Meta client can run many ads. Add <strong>one row per Lead Gen form</strong> — each form imports leads from every ad attached to that form.
            Reuse the same App ID and token across rows when forms share one Meta app.
          </p>
          <div style={grid}>
            <SaField label="Source name" hint="e.g. Sector 115 — Main form">
              <input
                name="integrationName"
                placeholder="Friendly label in SuperAdmin"
                value={form.integrationName}
                onChange={changeHandler}
                style={saInputSx}
              />
            </SaField>
            <SaField label="App ID *">
              <input name="appId" value={form.appId} onChange={changeHandler} style={{ ...saInputSx, borderColor: formError.appId ? SA.danger : SA.border }} />
              {formError.appId && typeof formError.appId === "string" ? (
                <p style={{ margin: "6px 0 0", fontSize: "12px", color: SA.danger }}>{formError.appId}</p>
              ) : null}
            </SaField>
            <SaField label="App secret *">
              <input
                name="appSecret"
                type="password"
                autoComplete="new-password"
                value={form.appSecret}
                onChange={changeHandler}
                style={{ ...saInputSx, borderColor: formError.appSecret ? SA.danger : SA.border }}
              />
            </SaField>
            <SaField label="Access token *">
              <input
                name="accessToken"
                type="password"
                autoComplete="new-password"
                value={form.accessToken}
                onChange={changeHandler}
                style={{ ...saInputSx, borderColor: formError.accessToken ? SA.danger : SA.border }}
              />
            </SaField>
            <SaField label="Lead Gen form ID *" hint="Not an Ad ID — use Discover forms below.">
              <input name="leadGenId" value={form.leadGenId} onChange={changeHandler} style={{ ...saInputSx, borderColor: formError.leadGenId ? SA.danger : SA.border }} />
            </SaField>
            <SaField label="Facebook Page ID (webhook)">
              <input
                name="facebookPageId"
                placeholder="Numeric Page ID"
                value={form.facebookPageId}
                onChange={changeHandler}
                style={saInputSx}
              />
            </SaField>
          </div>

          <SaFormActions>
            <SaGhostButton type="button" disabled={discovering} onClick={discoverForms}>
              <i className={discovering ? "fas fa-spinner fa-spin" : "fas fa-search"} style={{ marginRight: "8px" }} />
              {discovering ? "Loading forms…" : "Discover active forms from Page"}
            </SaGhostButton>
          </SaFormActions>
          {discoveredForms.length > 0 ? (
            <div style={{ marginTop: "14px", border: `1px solid ${SA.border}`, borderRadius: "10px", overflow: "hidden" }}>
              {discoveredForms.map((f) => (
                <div
                  key={f.formId ?? f.FormId}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 14px",
                    borderBottom: `1px solid ${SA.border}`,
                    fontSize: "13px",
                  }}
                >
                  <span>
                    <strong>{f.name ?? f.Name ?? "Form"}</strong>
                    <span style={{ color: SA.muted, marginLeft: "8px" }}>{f.formId ?? f.FormId}</span>
                    {f.leadsCount != null ? (
                      <span style={{ color: SA.muted, marginLeft: "8px" }}>({f.leadsCount} leads)</span>
                    ) : null}
                  </span>
                  <button type="button" onClick={() => applyDiscoveredForm(f)} style={{ fontSize: "12px", color: SA.primary, border: "none", background: "none", cursor: "pointer" }}>
                    Use this form
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          <div style={{ marginTop: "12px" }}>
            <SaField label="Scheduler / manual import">
              <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "14px", color: SA.ink }}>
                <input
                  type="checkbox"
                  name="isImportEnabled"
                  checked={form.isImportEnabled !== false}
                  onChange={changeHandler}
                  style={{ width: "18px", height: "18px" }}
                />
                Include this form in scheduled and “import all” runs
              </label>
            </SaField>
          </div>

          <SaSectionTitle iconClass="fas fa-cloud-download-alt">Lead import window</SaSectionTitle>
          <p style={{ margin: "-8px 0 12px", fontSize: "13px", color: SA.muted }}>
            Controls how far back manual and scheduled imports read from Facebook for this client. Leave days empty to use the system default from appsettings.
          </p>
          <div style={grid}>
            <SaField label="Import full history">
              <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "14px", color: SA.ink }}>
                <input
                  type="checkbox"
                  name="facebookLeadImportFullData"
                  checked={!!form.facebookLeadImportFullData}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setForm((prev) => ({
                      ...prev,
                      facebookLeadImportFullData: checked,
                      facebookLeadImportDaysBack: checked ? "" : prev.facebookLeadImportDaysBack,
                    }));
                    setFormError((prev) => ({ ...prev, facebookLeadImportDaysBack: "" }));
                  }}
                  style={{ width: "18px", height: "18px" }}
                />
                Fetch all leads (no date filter)
              </label>
            </SaField>
            <SaField label="Days back (when full history is off)" hint="Empty = Scheduler:FacebookLeadImportDaysBack in appsettings.">
              <input
                name="facebookLeadImportDaysBack"
                type="number"
                min={1}
                disabled={!!form.facebookLeadImportFullData}
                placeholder="e.g. 7"
                value={form.facebookLeadImportDaysBack}
                onChange={changeHandler}
                style={{
                  ...saInputSx,
                  borderColor: formError.facebookLeadImportDaysBack ? SA.danger : SA.border,
                  opacity: form.facebookLeadImportFullData ? 0.6 : 1,
                }}
              />
              {formError.facebookLeadImportDaysBack ? (
                <p style={{ margin: "6px 0 0", fontSize: "12px", color: SA.danger }}>{formError.facebookLeadImportDaysBack}</p>
              ) : null}
            </SaField>
          </div>

          <SaFormActions>
            <SaPrimaryButton type="submit" disabled={saving} iconClass={saving ? "fas fa-spinner fa-spin" : "fas fa-plus"}>
              {saving ? "Saving…" : "Add Lead Gen source"}
            </SaPrimaryButton>
          </SaFormActions>
        </SaCard>
      </form>

      <div style={{ marginTop: "16px" }}>
        <SaCard noPadding>
          <div
            style={{
              padding: "18px 22px",
              borderBottom: `1px solid ${SA.border}`,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <span style={{ fontSize: "14px", fontWeight: "800", color: SA.ink }}>Lead Gen sources ({fbIntegrations.length})</span>
            {fbIntegrations.length > 0 ? (
              <SaPrimaryButton
                type="button"
                disabled={importingId != null}
                iconClass={importingId === "all" ? "fas fa-spinner fa-spin" : "fas fa-download"}
                onClick={() => runFacebookImport(null)}
              >
                {importingId === "all" ? "Importing…" : "Import all enabled forms"}
              </SaPrimaryButton>
            ) : null}
          </div>
          {fbIntegrations.length === 0 ? (
            <div style={{ padding: "24px", textAlign: "center", color: SA.muted }}>
              No Facebook integrations for this client yet.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table ref={tableRef} className="table mb-0" style={{ width: "100%" }}>
                <thead style={{ background: SA.bgSoft }}>
                  <tr>
                    {["Name", "Form ID", "Import", "Page ID", "Import window", "Actions"].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "12px 16px",
                          fontSize: "11px",
                          fontWeight: "800",
                          color: SA.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fbIntegrations.map((row) => (
                    <tr key={row.id}>
                      <td style={{ padding: "12px 16px", fontSize: "13px", color: SA.ink }}>
                        {row.integrationName || row.IntegrationName || "—"}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: "13px", color: SA.muted }}>{shorten(row.leadGenId, 18)}</td>
                      <td style={{ padding: "12px 16px", fontSize: "13px", color: SA.muted }}>
                        {row.isImportEnabled === false ? "Off" : "On"}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: "13px", color: SA.muted }}>
                        {shorten(row.facebookPageId ?? row.FacebookPageId, 18)}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: "13px", color: SA.muted }}>{importModeLabel(row)}</td>
                      <td style={{ padding: "12px 16px", textAlign: "center" }}>
                        <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                          <button
                            type="button"
                            title="Import this form"
                            disabled={importingId != null || row.isImportEnabled === false}
                            onClick={() => runFacebookImport(row.id)}
                            style={{
                              border: `1px solid ${SA.border}`,
                              background: "#fff",
                              borderRadius: "8px",
                              width: "36px",
                              height: "36px",
                              cursor: importingId != null ? "not-allowed" : "pointer",
                              color: SA.primary,
                            }}
                          >
                            <i className={importingId === row.id ? "fas fa-spinner fa-spin" : "fas fa-download"} />
                          </button>
                          <button
                            type="button"
                            title="Edit"
                            onClick={() => navigate(`/editfbintegration/${row.id}`)}
                            style={{
                              border: `1px solid ${SA.border}`,
                              background: "#fff",
                              borderRadius: "8px",
                              width: "36px",
                              height: "36px",
                              cursor: "pointer",
                              color: SA.primary,
                            }}
                          >
                            <i className="fas fa-pen" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SaCard>
      </div>
    </div>
  );
}

export default AddFBIntegtarion;
